/**
 * AppNavList — renders the role-aware navigation list inside the AppShell rail.
 *
 * Items are sourced from `currentUser.menu` (JWT claims), filtered through
 * `hasPermission(..., PermissionAction.GET)`.  Submenus expand/collapse via
 * MUI <Collapse>.  All display strings go through `t()`.
 *
 * Styling follows mockup `04-admin-portal`: the rail is navy in BOTH color
 * modes, so every foreground value comes from ITOW_RAIL rather than the
 * mode-dependent palette. Sub-items sit behind a vertical guide hairline with
 * a small dot marker that lights up on the active row.
 *
 * Icon strategy (A): `<Icon>{name}</Icon>` from `@mui/material` — works for any
 * backend-supplied icon name without a front-end code change.  Requires the
 * Material Icons CSS font to be loaded in index.html.
 *
 * Names go through `resolveNavIcon` (see ./navIcon) because the menu payload
 * mixes vocabularies: most rows are Material ligatures, but some are Font
 * Awesome (`fa-house`), which <Icon> cannot resolve and which rendered as an
 * empty icon gutter. Icons render in the outlined variant.
 *
 * Top-level rows are grouped into sections (operations / setup) with an
 * eyebrow above each, suppressed when only one section is present.
 */
import { Fragment, useState } from 'react';
import {
  Box,
  Icon,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MenuItem } from '@/core/rbac/types';
import { hasPermission, PermissionAction } from '@/core/rbac';
import { useAuth, normalizeUserType } from '@/core/auth';
import { useNavigationGuard } from './navigationGuardContext';
import { ITOW_RAIL, ITOW_SURFACE } from '@/theme/theme';
import {
  resolveNavIcon,
  sectionForRoute,
  isDashboardRoute,
  SECTION_LABEL_KEY,
  type NavSection,
} from './navIcon';

interface AppNavListProps {
  menu: MenuItem[];
  onNavigate?: () => void;
  /** When true the drawer is in icon-only (collapsed) mode — hide text labels. */
  compact?: boolean;
}

/** Backend menu route for the chat page, nested under Catalogs. */
const CHAT_ROUTE = 'administrator-chat';

/** Front-end-only route — the backend menu has no entry for it. */
const PROVIDER_PROFILE_ROUTE = 'provider-profile';

/** Admin-side rollup of every provider's rates. */
const PROVIDER_PRICING_ROUTE = 'provider-pricing';

/** A provider's own wreckers and recovery gear, and the admin rollup of it. */
const PROVIDER_EQUIPMENT_ROUTE = 'provider-equipment';
const EQUIPMENT_COVERAGE_ROUTE = 'equipment-coverage';

/** The network on a map — who covers where, and what they can do there. */
const PROVIDER_MAP_ROUTE = 'provider-map';

/** Who would be offered a given tow, in order, and why everyone else was not. */
const PROVIDER_RANKING_ROUTE = 'provider-ranking';

/** Is the bidding loop working — platform-wide analytics. */
const REPORTS_ROUTE = 'reports';

/** What is wrong with the product right now, worst first. */
const ISSUES_ROUTE = 'issues';

/** Every workflow, step by step, with the gaps marked. */
const WORKFLOWS_ROUTE = 'workflows';

/**
 * Lifts Administrator Chat out of Catalogs and up to a top-level row.
 *
 * The backend menu files it under Catalogs, alongside Customers, Drivers and
 * Lookup Types — reference data you edit occasionally. Chat is nothing like
 * that: it is live work with an unread count on the dashboard, and burying an
 * inbox two clicks deep behind a maintenance menu is why nobody found it.
 *
 * Done here rather than in the menu table so it does not need a data migration
 * and cannot break other clients reading the same menu. If the backend ever
 * promotes it, `existing` catches the duplicate and this becomes a no-op.
 */
function promoteChat(items: MenuItem[], fullMenu: MenuItem[]): MenuItem[] {
  const slug = (route: string) => route.replace(/^\/+|\/+$/g, '').split('/')[0].toLowerCase();
  const trim = (route: string) => route.replace(/^\/+|\/+$/g, '');
  if (items.some((item) => item.unique && slug(item.route) === CHAT_ROUTE)) return items;

  let chat: MenuItem | null = null;
  const rest = items.map((item) => {
    const subs = item.subMenus ?? [];
    const idx = subs.findIndex((sub) => slug(sub.route) === CHAT_ROUTE);
    if (idx === -1) return item;
    const found = subs[idx];
    // The permission lives on the submenu under its own relative route, so it
    // has to be read before promotion rewrites that route to a full path --
    // afterwards findMenuItem would match nothing and the row would silently
    // vanish for everyone.
    if (!hasPermission(fullMenu, found.route, PermissionAction.GET)) return item;
    chat = {
      ...found,
      // A top-level row navigates by its own route, so it has to carry the
      // full path the submenu only had relative to its parent.
      route: `${trim(item.route)}/${trim(found.route)}`,
      unique: true,
      subMenus: [],
      // Same name and glyph as the synthesised row below.
      //
      // Only administrators have this row in their backend menu, and it
      // arrives labelled "Administrator Chat" with a `chat` icon, while every
      // other role gets the fallback's "Messages" and `forum`. So the one
      // person most likely to be told "check your messages" was the only one
      // without a row saying Messages — it was reported as the link being
      // missing from the admin nav, by someone looking straight at it.
      icon: 'forum',
      iconType: 'material-icons',
      labelTranslate: 'CHAT.title',
      name: 'Messages',
    };
    return { ...item, subMenus: subs.filter((_, i) => i !== idx) };
  });

  // Only administrators get chat in their backend menu, but every role can use
  // the page — a customer needs to reach the help desk as much as the help desk
  // needs to reach them. Synthesise the row when the menu has none.
  //
  // Safe to bypass hasPermission here: this runs *after* the permission filter,
  // and access is enforced server-side anyway — `threads` returns only the
  // caller's own conversations whoever asks.
  if (chat === null) {
    chat = {
      id: -900,
      name: 'Messages',
      route: `catalogs/${CHAT_ROUTE}`,
      icon: 'forum',
      iconType: 'material-icons',
      selected: false,
      permission: [1, 2, 3, 4],
      unique: true,
      labelTranslate: 'CHAT.title',
      subMenus: [],
    } as MenuItem;
  }

  // Ahead of the first setup row, so it lands among the operational rows.
  const firstSetup = rest.findIndex((item) => sectionForRoute(item.route) === 'setup');
  const at = firstSetup === -1 ? rest.length : firstSetup;
  return [...rest.slice(0, at), chat, ...rest.slice(at)];
}

/**
 * Gives tow providers a Profile & Pricing row.
 *
 * Their company details and rate card used to live in a Google Form answered
 * once at signup, so there was nowhere in the product to correct a rate or a
 * lapsed certificate. It is Setup rather than Operations: reference data you
 * maintain, not work you do today.
 *
 * Synthesised because the backend menu has no entry for it. Same reasoning as
 * the chat row — this runs after the permission filter, and the server decides
 * whose card you get from your token regardless of what the client asks for.
 */
function addProviderProfile(items: MenuItem[], userType: string | null): MenuItem[] {
  // Administrators get the network-wide rollup instead of a card of their own —
  // they have no rates, they compare everyone else's.
  if (userType === 'ADMIN') {
    if (items.some((i) => i.route.replace(/^\/+|\/+$/g, '') === PROVIDER_PRICING_ROUTE)) return items;
    return [
      ...items,
      {
        id: -902,
        name: 'Provider Pricing',
        route: PROVIDER_PRICING_ROUTE,
        icon: 'price_change',
        iconType: 'material-icons',
        selected: false,
        permission: [1, 2, 3, 4],
        unique: true,
        labelTranslate: 'PROVIDER-PRICING.navLabel',
        subMenus: [],
      } as MenuItem,
      {
        id: -903,
        name: 'Equipment Coverage',
        route: EQUIPMENT_COVERAGE_ROUTE,
        icon: 'precision_manufacturing',
        iconType: 'material-icons',
        selected: false,
        permission: [1, 2, 3, 4],
        unique: true,
        labelTranslate: 'PROVIDER-EQUIPMENT.coverage.navLabel',
        subMenus: [],
      } as MenuItem,
      {
        id: -905,
        name: 'Provider Map',
        route: PROVIDER_MAP_ROUTE,
        icon: 'map',
        iconType: 'material-icons',
        selected: false,
        permission: [1, 2, 3, 4],
        unique: true,
        labelTranslate: 'PROVIDER-MAP.navLabel',
        subMenus: [],
      } as MenuItem,
      {
        id: -907,
        name: 'Provider Ranking',
        route: PROVIDER_RANKING_ROUTE,
        icon: 'format_list_numbered',
        iconType: 'material-icons',
        selected: false,
        permission: [1, 2, 3, 4],
        unique: true,
        labelTranslate: 'PROVIDER-RANKING.navLabel',
        subMenus: [],
      } as MenuItem,
      {
        id: -906,
        name: 'KPIs & Reports',
        route: REPORTS_ROUTE,
        icon: 'insights',
        iconType: 'material-icons',
        selected: false,
        permission: [1, 2, 3, 4],
        unique: true,
        labelTranslate: 'REPORTS.navLabel',
        subMenus: [],
      } as MenuItem,
      {
        id: -908,
        name: 'Workflows',
        route: WORKFLOWS_ROUTE,
        icon: 'account_tree',
        iconType: 'material-icons',
        selected: false,
        permission: [1, 2, 3, 4],
        unique: true,
        labelTranslate: 'WORKFLOWS.navLabel',
        subMenus: [],
      } as MenuItem,
      {
        id: -909,
        name: 'Open Issues',
        route: ISSUES_ROUTE,
        icon: 'report_problem',
        iconType: 'material-icons',
        selected: false,
        permission: [1, 2, 3, 4],
        unique: true,
        labelTranslate: 'ISSUES.navLabel',
        subMenus: [],
      } as MenuItem,
    ];
  }

  if (userType !== 'TOWPROVIDER') return items;
  if (items.some((i) => i.route.replace(/^\/+|\/+$/g, '') === PROVIDER_PROFILE_ROUTE)) return items;

  return [
    ...items,
    {
      id: -901,
      name: 'Profile & Pricing',
      route: PROVIDER_PROFILE_ROUTE,
      icon: 'request_quote',
      iconType: 'material-icons',
      selected: false,
      permission: [1, 2, 3, 4],
      unique: true,
      labelTranslate: 'PROVIDER-PROFILE.navLabel',
      subMenus: [],
    } as MenuItem,
    {
      id: -904,
      name: 'Equipment',
      route: PROVIDER_EQUIPMENT_ROUTE,
      icon: 'precision_manufacturing',
      iconType: 'material-icons',
      selected: false,
      permission: [1, 2, 3, 4],
      unique: true,
      labelTranslate: 'PROVIDER-EQUIPMENT.navLabel',
      subMenus: [],
    } as MenuItem,
  ];
}

/**
 * Pulls Roadside Service up to sit directly beneath Towing Requests.
 *
 * They are the same job — a vehicle that needs help at the roadside — split
 * only by which service type it is, and they share a board, a provider pool
 * and a bidding loop. The backend menu happens to list roadside last, which
 * left the two halves of one workflow at opposite ends of the rail.
 *
 * Everything else keeps backend order.
 */
function slugOf(route: string): string {
  return route.replace(/^\/+|\/+$/g, '').split('/')[0].toLowerCase();
}

/** Moves the row matching `move` to sit directly after the row matching `anchor`. */
function hoistAfter(
  items: MenuItem[],
  move: (item: MenuItem) => boolean,
  anchor: (item: MenuItem) => boolean,
): MenuItem[] {
  const anchorIndex = items.findIndex(anchor);
  const moveIndex = items.findIndex(move);
  if (anchorIndex === -1 || moveIndex === -1 || moveIndex === anchorIndex + 1) return items;

  const moved = items[moveIndex];
  const rest = items.filter((_, idx) => idx !== moveIndex);
  // The anchor shifts one slot left if the moved row sat before it.
  const insertAt = (moveIndex < anchorIndex ? anchorIndex - 1 : anchorIndex) + 1;
  return [...rest.slice(0, insertAt), moved, ...rest.slice(insertAt)];
}

function orderOperations(section: NavSection, items: MenuItem[], userType: string | null): MenuItem[] {
  if (section !== 'operations') return items;

  // Roadside sits directly beneath Towing Requests. They are the same job — a
  // vehicle that needs help at the roadside — split only by service type, and
  // they share a board, a provider pool and a bidding loop. The backend menu
  // lists roadside last, which left the two halves of one workflow at opposite
  // ends of the rail.
  let ordered = hoistAfter(
    items,
    (i) => slugOf(i.route) === 'roadside-assistance',
    (i) => slugOf(i.route) === 'tow-requests',
  );

  // For a customer, Routings goes directly under Home. A fleet plans routes for
  // its own trucks daily; it files a tow only when something has gone wrong.
  // Scoped to customers because for everyone else routing is downstream of a
  // job, not the day's main work.
  if (userType === 'CUSTOMER') {
    // Home is matched via isDashboardRoute, not a literal slug: the backend
    // ships it as "/home" while the dev token ships it as "", and both mean
    // the dashboard.
    ordered = hoistAfter(
      ordered,
      (i) => slugOf(i.route) === 'routings',
      (i) => isDashboardRoute(i.route),
    );
  }

  return ordered;
}

/** Trims leading and trailing slashes then joins segments with exactly one '/'. */
function buildPath(...segments: string[]): string {
  return (
    '/' +
    segments
      .map((s) => s.replace(/^\/+|\/+$/g, ''))
      .filter(Boolean)
      .join('/')
  );
}

/** Top-level nav row — leaf links and expandable parents share this shape. */
const navItemSx = {
  position: 'relative',
  minHeight: 42,
  borderRadius: `${ITOW_SURFACE.navRadius}px`,
  mx: 1,
  px: 1.375,
  py: 1,
  color: ITOW_RAIL.ink,
  transition: 'background-color .12s ease, color .12s ease',
  '& .MuiListItemIcon-root': { color: ITOW_RAIL.ink, opacity: 0.75 },
  '&:hover': {
    backgroundColor: ITOW_RAIL.hoverBg,
    color: '#fff',
    '& .MuiListItemIcon-root': { opacity: 1 },
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: ITOW_RAIL.activeBg,
    color: '#fff',
    '& .MuiListItemIcon-root': { color: ITOW_RAIL.activeAccent, opacity: 1 },
    // Left accent bar. The background wash alone was not a legible "you are
    // here" signal against navy — see ITOW_RAIL.activeAccent.
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 8,
      bottom: 8,
      width: 3,
      borderRadius: '0 3px 3px 0',
      backgroundColor: ITOW_RAIL.activeAccent,
    },
  },
  // Keyboard focus must stay visible against the navy (a11y).
  '&.Mui-focusVisible': { outline: `2px solid ${ITOW_RAIL.activeAccent}`, outlineOffset: -2 },
} as const;

/**
 * Nav icons render outlined. The filled set is visually heavy at rail size and
 * is the main reason the sidebar read as dated; the outlined variant is the
 * same ligature names, so nothing else changes.
 */
const NAV_ICON_CLASS = 'material-icons-outlined';

export default function AppNavList({ menu, onNavigate, compact = false }: AppNavListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentUser } = useAuth();
  const { attemptNavigate } = useNavigationGuard();
  const userType = currentUser === null ? null : normalizeUserType(currentUser.userType);

  // Per-item expand state keyed by MenuItem.id — all collapsed by default.
  const [openState, setOpenState] = useState<Record<number, boolean>>({});

  const toggleOpen = (id: number) => {
    setOpenState((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNavigate = (path: string) => {
    // Through the guard: a page with unsaved work gets to ask first. Without
    // one registered this runs `proceed` immediately.
    attemptNavigate(() => {
      navigate(path);
      onNavigate?.();
    });
  };

  // Filter top-level items the current user can read.
  const visibleItems = menu.filter((item) =>
    hasPermission(menu, item.route, PermissionAction.GET)
  );

  // A parent with no accessible children renders nothing (see the early return
  // further down), so drop those *before* grouping — otherwise a section could
  // print an eyebrow with no rows beneath it.
  const renderableItems = addProviderProfile(
    promoteChat(
      visibleItems.filter(
        (item) =>
          item.unique ||
          (item.subMenus ?? []).some((sub) =>
            hasPermission(menu, sub.route, PermissionAction.GET),
          ),
      ),
      menu,
    ),
    userType,
  );

  if (renderableItems.length === 0) {
    return null;
  }

  // Bucket into exactly one group per section, preserving backend order
  // within each.
  //
  // This previously started a new group every time the section *changed* while
  // walking the menu, so an interleaved payload — ops, setup, ops — printed the
  // OPERATIONS eyebrow twice with unrelated rows underneath, and handed React
  // two <Fragment>s keyed "operations". That is exactly what the live menu
  // does: Roadside Service arrives after Catalogs and Configuration, so it sat
  // alone under a second OPERATIONS heading, orphaned from the other
  // day-to-day rows.
  const bySection = new Map<NavSection, MenuItem[]>();
  for (const item of renderableItems) {
    const section = sectionForRoute(item.route);
    const bucket = bySection.get(section);
    if (bucket) bucket.push(item);
    else bySection.set(section, [item]);
  }

  // Operations before setup: the work comes first, the configuration that
  // supports it second. Fixed rather than first-seen so the rail does not
  // reorder itself when the backend menu changes.
  const SECTION_ORDER: NavSection[] = ['operations', 'setup'];
  const groups = SECTION_ORDER.flatMap((section) => {
    const items = bySection.get(section);
    return items ? [{ section, items: orderOperations(section, items, userType) }] : [];
  });

  // A lone "OPERATIONS" heading over the entire list is noise, and the
  // collapsed rail has no room for text at all.
  const showEyebrows =
    !compact && new Set(groups.map((g) => g.section)).size > 1;

  const renderItem = (item: MenuItem) => {
        // A parent menu is open when:
        //   (a) the user explicitly toggled it via the chevron, OR
        //   (b) the current URL path matches one of its sub-menu routes
        //       (so the section auto-expands when you land on a child page).
        // The user's explicit toggle still wins (Record value `true` keeps it
        // open even if you navigate away; `false` keeps it closed even when
        // a child is active).
        const hasActiveChild = (item.subMenus ?? []).some(
          (sub) => pathname === buildPath(item.route, sub.route),
        );
        const isOpen = openState[item.id] ?? hasActiveChild;

        if (item.unique) {
          // ── Leaf link ──────────────────────────────────────────────────
          const path = buildPath(item.route);
          // "/home" redirects to "/" — see isDashboardRoute.
          const selected =
            pathname === path || (pathname === '/' && isDashboardRoute(item.route));

          return (
            <ListItem key={item.id} disablePadding>
              <Tooltip title={compact ? t(item.labelTranslate) : ''} placement="right" disableHoverListener={!compact} disableFocusListener={!compact} disableTouchListener={!compact}>
                <ListItemButton
                  selected={selected}
                  aria-current={selected ? 'page' : undefined}
                  onClick={() => handleNavigate(path)}
                  sx={{ ...navItemSx, justifyContent: compact ? 'center' : 'flex-start' }}
                >
                  <ListItemIcon sx={{ minWidth: compact ? 'auto' : 34 }}>
                    <Icon baseClassName={NAV_ICON_CLASS} sx={{ fontSize: '1.2rem' }}>{resolveNavIcon(item.icon)}</Icon>
                  </ListItemIcon>
                  {!compact && (
                    <ListItemText
                      primary={t(item.labelTranslate)}
                      primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 600 }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        }

        // ── Expandable parent with submenus ────────────────────────────
        // `subMenus` is `[]` for unique-true items in current builds, but
        // older API tokens may have shipped `null`; default to empty.
        const visibleSubMenus = (item.subMenus ?? []).filter((sub) =>
          hasPermission(menu, sub.route, PermissionAction.GET)
        );

        // Hide the parent entirely if it has no accessible children.
        if (visibleSubMenus.length === 0) {
          return null;
        }

        const expandAriaLabel = isOpen
          ? t('nav.collapseSubmenu', { name: item.name })
          : t('nav.expandSubmenu', { name: item.name });

        return (
          <ListItem key={item.id} disablePadding sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
            {/* Parent button — toggles submenu; does NOT navigate */}
            <Tooltip title={compact ? t(item.labelTranslate) : ''} placement="right" disableHoverListener={!compact} disableFocusListener={!compact} disableTouchListener={!compact}>
              <ListItemButton
                onClick={() => toggleOpen(item.id)}
                aria-expanded={isOpen}
                aria-label={expandAriaLabel}
                sx={{ ...navItemSx, justifyContent: compact ? 'center' : 'flex-start' }}
              >
                <ListItemIcon sx={{ minWidth: compact ? 'auto' : 34 }}>
                  <Icon baseClassName={NAV_ICON_CLASS} sx={{ fontSize: '1.2rem' }}>{resolveNavIcon(item.icon)}</Icon>
                </ListItemIcon>
                {!compact && (
                  <>
                    <ListItemText
                      primary={t(item.labelTranslate)}
                      primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 600 }}
                    />
                    <ChevronRightIcon
                      sx={{
                        fontSize: '0.95rem',
                        opacity: 0.6,
                        transition: 'transform .15s',
                        transform: isOpen ? 'rotate(90deg)' : 'none',
                      }}
                    />
                  </>
                )}
              </ListItemButton>
            </Tooltip>

            {/* Plain conditional render — deliberately NOT <Collapse>.
                The animated version drove an infinite render loop
                ("Maximum update depth exceeded") whenever the submenu went
                closed→open as a result of navigation rather than a click: e.g.
                the dashboard's "New Adjuster" KPI card jumping straight to
                /catalogs/adjusters auto-expands Catalogs, and the enter
                transition never settled. Clicking the same item in the sidebar
                was fine because the section was already open, so no transition
                ran — which is exactly the asymmetry Colt spotted.
                A height animation on a nav that is usually already in the right
                state is not worth a class of bug that takes the whole page
                down, so the submenu simply renders. */}
            {/* The Box carries the guide hairline that runs behind the
                sub-items, aligned under the parent icon (mockup 04). */}
            {isOpen && (
              <Box
                sx={{
                  position: 'relative',
                  pl: compact ? 0 : 1.25,
                  '&::before': compact
                    ? undefined
                    : {
                        content: '""',
                        position: 'absolute',
                        left: 20,
                        top: 2,
                        bottom: 6,
                        width: '1.5px',
                        backgroundColor: ITOW_RAIL.hairline,
                      },
                }}
              >
                <List disablePadding>
                  {visibleSubMenus.map((sub) => {
                    const subPath = buildPath(item.route, sub.route);
                    const subSelected = pathname === subPath;

                    return (
                      <ListItem key={sub.id} disablePadding>
                        <Tooltip title={compact ? t(sub.labelTranslate) : ''} placement="right" disableHoverListener={!compact} disableFocusListener={!compact} disableTouchListener={!compact}>
                          <ListItemButton
                            selected={subSelected}
                            aria-current={subSelected ? 'page' : undefined}
                            onClick={() => handleNavigate(subPath)}
                            sx={{
                              minHeight: 36,
                              borderRadius: `${ITOW_SURFACE.navRadius - 1}px`,
                              mx: 1,
                              pl: compact ? 1 : 3.75,
                              pr: 1.25,
                              py: 0.875,
                              color: ITOW_RAIL.muted,
                              '&:hover': { color: ITOW_RAIL.ink, backgroundColor: ITOW_RAIL.hoverBg },
                              '&.Mui-selected, &.Mui-selected:hover': {
                                backgroundColor: ITOW_RAIL.activeSubBg,
                                color: '#fff',
                                '& .itow-subdot': { backgroundColor: ITOW_RAIL.marker, opacity: 1 },
                              },
                              '&.Mui-focusVisible': {
                                outline: `2px solid ${ITOW_RAIL.marker}`,
                                outlineOffset: -2,
                              },
                            }}
                          >
                            {compact ? (
                              <ListItemIcon sx={{ minWidth: 'auto', color: 'inherit' }}>
                                <Icon baseClassName={NAV_ICON_CLASS} sx={{ fontSize: '1.05rem' }}>{resolveNavIcon(sub.icon)}</Icon>
                              </ListItemIcon>
                            ) : (
                              <Box
                                className="itow-subdot"
                                sx={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: '50%',
                                  backgroundColor: 'currentColor',
                                  opacity: 0.6,
                                  flex: 'none',
                                  mr: 1.125,
                                }}
                              />
                            )}
                            {!compact && (
                              <ListItemText
                                primary={t(sub.labelTranslate)}
                                primaryTypographyProps={{ fontSize: '0.78125rem', fontWeight: 500 }}
                              />
                            )}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}
          </ListItem>
        );
  };

  return (
    <List sx={{ flexGrow: 1, pt: 1, px: 0.5 }}>
      {groups.map((group) => (
        <Fragment key={group.section}>
          {showEyebrows && (
            <Typography
              component="li"
              variant="overline"
              sx={{
                display: 'block',
                mt: 2,
                mb: 0.5,
                mx: 1,
                px: 1.375,
                color: ITOW_RAIL.eyebrow,
                fontSize: '0.625rem',
                fontWeight: 700,
                letterSpacing: '.09em',
                lineHeight: 1.6,
                // The first group sits directly under the masthead, which
                // already supplies the gap.
                '&:first-of-type': { mt: 0.5 },
              }}
            >
              {t(SECTION_LABEL_KEY[group.section])}
            </Typography>
          )}
          {group.items.map(renderItem)}
        </Fragment>
      ))}
    </List>
  );
}
